//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodyText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import {
  MARKETO_FORM_ID,
  MarketoIntegrationTrackingIds,
} from '@marketo-integration/marketoIntegrationConstants';
import { Mixins } from '@theme/mixins';

interface Props {
  closeModal: () => void;
  isLoadingForm: boolean;
  isModalOpen: boolean;
}

export const MarketoIntegrationModal = ({
  closeModal,
  isLoadingForm,
  isModalOpen,
}: Props) => {
  return (
    <Modal
      closeModal={closeModal}
      contents={
        <Container>
          <BodyText>
            Enter your information below and a Bazaarvoice team member will
            reach out.
          </BodyText>
          {isLoadingForm && <CenteredSpinner />}
          <StyledForm id={MARKETO_FORM_ID} />
        </Container>
      }
      isModalOpen={isModalOpen}
      primaryButtonInfo={{
        trackingId: MarketoIntegrationTrackingIds.MODAL_FORM_SUBMIT_BUTTON,
        onClick: () => {
          window.MktoForms2?.whenReady((form) => {
            form.submit();
          });
        },
        buttonContent: 'Submit',
      }}
      secondaryButtonInfo={{
        trackingId: MarketoIntegrationTrackingIds.MODAL_CANCEL_BUTTON,
      }}
      title='Learn more about additional Vibe features'
      trackingId={MarketoIntegrationTrackingIds.MODAL}
      width='wide'
    />
  );
};

const Container = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: column;
`;

// Important styles are required because Marketo adds inline style props to
// all form elements
const StyledForm = styled.form(
  ({ theme }) => `
  width: 100% !important;

  & input {
    width: calc(${theme.spacings[6]} * 12) !important;
  }

  & .mktoAsterix {
    color: ${theme.colors.aliases.bodyPrimary} !important;
  }

  & label {
    color: ${theme.colors.aliases.bodyPrimary} !important;
    padding: ${theme.spacings[2]} 0 ${theme.spacings[1]} !important;
  }

  /* Hide the form button to use modal Submit button as the form submit button */
  & button[type="submit"],
  & .mktoButtonRow {
    ${Mixins.VisuallyHidden};
  }
`,
);
